import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'

import { FreezeIcon, StreakFlame } from '@/components/streak-icons'
import { useGrowIn } from '@/hooks/use-grow-in'

// Jan 1 2024 is a Monday, matching the backend's Monday-first week.
const dayLetter = (lang: string, i: number) =>
  new Date(Date.UTC(2024, 0, 1 + i)).toLocaleDateString(lang, {
    weekday: 'narrow',
    timeZone: 'UTC',
  })

export function DayRing({
  done,
  total,
  label,
  size = 76,
}: {
  done: number
  total: number
  label?: string
  size?: number
}) {
  const { t } = useTranslation()
  const stroke = size >= 70 ? 7 : 5
  const r = size / 2 - stroke + 1
  const c = 2 * Math.PI * r
  const grown = useGrowIn()
  const pct = grown && total > 0 ? done / total : 0
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-muted"
        />
        {done > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${c * pct} ${c}`}
            className="stroke-primary motion-safe:transition-[stroke-dasharray] motion-safe:duration-700 motion-safe:ease-out"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span
          className={cn(
            'font-black tabular-nums text-foreground',
            size >= 70 ? 'text-[17px]' : 'text-[13px]',
          )}
        >
          {done}/{total}
        </span>
        <span
          className={cn(
            'text-muted-foreground',
            size >= 70 ? 'mt-1 text-[10px]' : 'mt-0.5 text-[8.5px]',
          )}
        >
          {label ?? t('today.progress.today')}
        </span>
      </div>
    </div>
  )
}

export function Stat({
  value,
  label,
  extra,
}: {
  value: React.ReactNode
  label: string
  extra?: React.ReactNode
}) {
  return (
    <div className="min-w-0">
      <p className="truncate text-base font-extrabold tabular-nums text-foreground">
        {value}
        {extra}
      </p>
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
    </div>
  )
}

// At-a-glance progress above Today's list: today's ring, streak, this week vs last,
// habits and goal pace, plus the week's activity bars. Opens Stats.
export function TodayProgressCard({
  done,
  total,
  habitsDone,
  habitsDue,
  streak,
  freezes,
  className,
}: {
  done: number
  total: number
  habitsDone: number
  habitsDue: number
  streak: number
  freezes: number
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const stats = useQuery(api.stats.getStats, { range: '7d' })
  const week = stats?.week
  const weekCount = week ? week.cur.tasks + week.cur.checkIns : null
  const prevCount = week ? week.prev.tasks + week.prev.checkIns : 0
  const delta =
    weekCount !== null && prevCount > 0
      ? Math.round(((weekCount - prevCount) / prevCount) * 100)
      : null
  const paced = stats?.goals.filter((g) => g.pace === 'onPace' || g.pace === 'behind') ?? []
  const onPace = paced.filter((g) => g.pace === 'onPace').length
  const max = Math.max(1, ...(week?.days ?? []))
  const grown = useGrowIn() && !!week

  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/stats' })}
      aria-label={t('today.progress.open')}
      className={cn(
        'block w-full cursor-pointer text-left transition-opacity hover:opacity-80',
        className,
      )}
    >
      <div className="flex items-center gap-4 pt-1">
        <DayRing done={done} total={total} />
        <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-2">
          <Stat
            value={
              <>
                <StreakFlame /> {streak}
              </>
            }
            extra={
              freezes > 0 && (
                <span className="ml-1 text-xs" title={t('today.streakFreezeTooltip')}>
                  {Array.from({ length: freezes }, (_, i) => (
                    <FreezeIcon key={i} />
                  ))}
                </span>
              )
            }
            label={t('today.progress.streak')}
          />
          <Stat
            value={weekCount ?? '–'}
            extra={
              delta !== null &&
              delta !== 0 && (
                <span
                  className={cn(
                    'ml-1.5 text-[11px] font-bold',
                    delta > 0 ? 'text-emerald-500' : 'text-muted-foreground',
                  )}
                >
                  {delta > 0 ? '▲' : '▼'}
                  {Math.abs(delta)}%
                </span>
              )
            }
            label={t('today.progress.thisWeek')}
          />
          <Stat value={`${habitsDone}/${habitsDue}`} label={t('today.progress.habits')} />
          <Stat
            value={paced.length > 0 ? `${onPace}/${paced.length}` : (stats?.goalsActive ?? '–')}
            label={t(paced.length > 0 ? 'today.progress.onPace' : 'today.progress.activeGoals')}
          />
        </div>
      </div>
      <div className="pt-2.5 pb-1">
        <div className="flex h-8 items-end gap-1">
          {Array.from({ length: 7 }, (_, i) => {
            const v = week?.days[i] ?? 0
            const isToday = week ? i === week.elapsed - 1 : false
            const future = week ? i >= week.elapsed : true
            return (
              <span
                key={i}
                className={cn(
                  'flex-1 rounded-[3px] motion-safe:transition-[height] motion-safe:duration-500 motion-safe:ease-out',
                  v > 0 && !future ? 'bg-primary' : 'bg-muted',
                  isToday && 'ring-1 ring-foreground/70 ring-offset-1 ring-offset-card',
                )}
                style={{
                  height: `${Math.max(12, grown ? (v / max) * 100 : 0)}%`,
                  transitionDelay: `${i * 40}ms`,
                }}
              />
            )
          })}
        </div>
        <div className="mt-1 flex gap-1">
          {Array.from({ length: 7 }, (_, i) => (
            <span
              key={i}
              className={cn(
                'flex-1 text-center text-[9px] text-muted-foreground/70',
                week && i === week.elapsed - 1 && 'font-bold text-foreground',
              )}
            >
              {dayLetter(i18n.language, i)}
            </span>
          ))}
        </div>
      </div>
    </button>
  )
}

// Compact Today's stand-in for the card: one muted line, zero/unknown parts omitted.
export function TodayProgressLine({
  done,
  total,
  streak,
  className,
}: {
  done: number
  total: number
  streak: number
  className?: string
}) {
  const { t } = useTranslation()
  const week = useQuery(api.stats.getStats, { range: '7d' })?.week
  const weekCount = week ? week.cur.tasks + week.cur.checkIns : 0
  const parts = [
    total > 0 && t('today.compactLine.today', { done, total }),
    streak > 0 && t('today.compactLine.streak', { count: streak }),
    weekCount > 0 && t('today.compactLine.week', { count: weekCount }),
  ].filter(Boolean)
  if (!parts.length) return null
  return <p className={cn('text-xs text-muted-foreground', className)}>{parts.join(' · ')}</p>
}
